
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

import { checkSmsAvailability, isValidUsPhoneNumber } from '@/checkout/helpers/sms'
import FormInput2 from '@/components/forms/FormInput2.vue'
import { logInTrackJS } from '@/errors/helpers'
import { environment } from '@/helpers/Environment'

@Component({
  name: 'SmsOptIn',
  components: { FormInput2 },
})
export default class extends Vue {
  @Prop({ required: true })
  value: boolean

  @Prop({ required: true })
  phoneNumber: string

  @Prop({ default: false })
  isMember: boolean

  private smsAvailable = false
  private checking = false
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private requestGeneration = 0

  get optedIn(): boolean {
    return this.value
  }

  set optedIn(v: boolean) {
    this.$emit('input', v)
  }

  get showCheckbox(): boolean {
    return this.smsAvailable && !this.checking
  }

  @Watch('phoneNumber', { immediate: true })
  onPhoneNumberChange(): void {
    this.optedIn = false
    this.smsAvailable = false

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    if (this.isValidUsNumber) {
      this.checking = true
    } else {
      this.checking = false
      return
    }

    // TODO Verify debounce timing — adds latency to the first check even on paste.
    // Consider triggering immediately on first valid number, then debouncing subsequent changes.
    this.debounceTimer = setTimeout(() => {
      this.checkAvailability()
    }, 250)
  }

  get isValidUsNumber(): boolean {
    return isValidUsPhoneNumber(this.phoneNumber)
  }

  /**
   *  The phone number can change while an API call is in-flight, so we need to ensure only
   *  the response from the most recent request updates the UI. A monotonically increasing
   *  generation counter identifies each request; stale responses are detected when their
   *  captured generation no longer matches the current one.
   *
   *  AbortController would also solve this and would additionally cancel the in-flight HTTP
   *  request, but the shared API layer (API.ts → requestAndRetryWithAxios) doesn't currently
   *  accept a signal. Adding one would require: extending ApiArgs with `signal?: AbortSignal`,
   *  forwarding it through `request()` into the axios config, and updating
   *  `checkSmsAvailability` to accept and pass it. The retry logic in
   *  `requestAndRetryWithAxios` would also need to account for abort-signal cancellations
   *  (`ERR_CANCELED`) vs network cancellations (`ECONNABORTED`) that it already handles.
   */
  private checkAvailability() {
    const generation = ++this.requestGeneration
    checkSmsAvailability(this.phoneNumber)
      .then((result) => {
        // Discard the result if a newer request has been initiated since this one started.
        if (generation !== this.requestGeneration) return
        this.smsAvailable = result.smsAvailable
      })
      .catch((error) => {
        logInTrackJS('SMS availability check failed', {
          error,
          numberChangedWhileChecking: generation !== this.requestGeneration,
        })
      })
      .finally(() => {
        // Only clear the loading state if this is still the latest request.
        // If a newer request is in-flight, keep `checking` true so the loader stays visible.
        if (generation === this.requestGeneration) {
          this.checking = false
        }
      })
  }

  get portalName(): string {
    return environment.portal.name
  }

  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }
}
